<!--
  RichTextEditor.vue

  Editor de texto formatado do Litterae, sobre Tiptap.

  ⚠️ NÃO substitui o CustomTextarea. Ainda serve ~21 campos de texto plano do
  sistema (bio de autor, e-mail de suporte, descrição de grupo e de permissão,
  observação de nota…), cujas colunas são texto puro e cuja exibição é texto
  puro. Transformá-los em rich text faria o usuário ver `<strong>` literal na
  tela. Descrição de atividade SAIU dessa lista em 24/08/2026 — ver
  `docs/activity-descriptions-html.md`.

  Este componente serve onde HTML é suportado de ponta a ponta: conteúdo de
  texto com content_format = 'html', e descrição de Activity/ActivityGeneral
  (que não tem essa distinção — grava HTML sempre, ver o documento acima).

  O contrato que ele obedece está em `docs/refatoracao-conteudo-html.md`: a
  whitelist do HtmlNormalizer e o vocabulário fechado de classes. A configuração
  vive em `utils/tiptapEditorConfig.js`, e o que a barra oferece é verificado pela
  suite de contrato (`tests/editor-contract/`).

  v-model é HTML, não texto.
-->
<script setup>
import { ref, computed, watch, onBeforeUnmount } from "vue";
import { useEditor, EditorContent } from "@tiptap/vue-3";
import { buildEditorExtensions } from "@/utils/tiptapEditorConfig";
import { toPlainText } from "@/utils/htmlProjection";
import { unwrapPdfLineBreaks } from "@/utils/stringUtils";
import RichTextToolbar from "./RichTextToolbar.vue";

const props = defineProps({
  /** Conteúdo em HTML. */
  modelValue: { type: String, default: "" },
  disabled: { type: Boolean, default: false },
  placeholder: { type: String, default: "Digite ou cole o conteúdo…" },
  /** Altura mínima da área editável fora do modo expandido. */
  minHeight: { type: String, default: "18rem" },
  /**
   * Teto da área editável fora do modo expandido. Sem ele a caixa cresce com o
   * conteúdo e o `overflow-y: auto` do `.rte-content` nunca dispara.
   *
   * Existe separado do `startCollapsed` de propósito: aquele traz o teto ATADO à
   * seta de colapso, e há quem queira a caixa de tamanho fixo sem oferecer o
   * gesto — caixa que só confere um conteúdo, não que se edita por muito tempo.
   * Ignorado quando `startCollapsed` está ligado, que governa o teto por conta.
   */
  maxHeight: { type: String, default: null },
  /**
   * Desfaz quebra dura herdada de PDF ao colar TEXTO PURO. Espelha o
   * `:unwrapPastedLineBreaks` do CustomTextarea, que o AddTextForm já usa.
   */
  unwrapPastedLineBreaks: { type: Boolean, default: false },
  /** Limite de caracteres **da projeção**. Só exibe; não bloqueia digitação. */
  maxLength: { type: Number, default: null },
  /**
   * Nasce com 50% de `minHeight`, com uma seta na base para expandir ao
   * tamanho final. Existe para formulário curto (hoje, os de Atividade):
   * a altura cheia do editor força rolagem numa tela de notebook antes
   * mesmo de o usuário chegar a usá-lo. A altura final não muda — só a
   * inicial.
   */
  startCollapsed: { type: Boolean, default: false },
  /**
   * Altura da caixa colapsada. Só vale com `startCollapsed`.
   *
   * O padrão é metade de `minHeight`, que é uma proporção — serve quando a
   * altura cheia já é a medida certa. Quando o pedido é em LINHAS de texto
   * ("mostre quatro"), a proporção não expressa isso: passe a altura direto.
   */
  collapsedHeight: { type: String, default: null },
});

const emit = defineEmits(["update:modelValue"]);

const isExpanded = ref(false);

// Colapso inicial (não confundir com `isExpanded`, o modo tela cheia acima):
// aqui é só a altura de largada, e só existe enquanto ninguém clicou na seta.
const isCollapsed = ref(props.startCollapsed);

function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value;
}

const collapsedHeight = computed(
  () => props.collapsedHeight ?? `calc(${props.minHeight} * 0.5)`
);

const contentMinHeight = computed(() => {
  if (isExpanded.value) return undefined;
  if (isCollapsed.value) return collapsedHeight.value;
  return props.minHeight;
});

// Teto da caixa, só quando `startCollapsed` está em jogo. Sem ele, `.rte-content`
// só tinha PISO (min-height): crescia com o conteúdo sem limite, e o
// `overflow-y: auto` nunca disparava — a rolagem interna nunca existiu, e é
// exatamente ela que evita que a caixa empurre o formulário e crie rolagem
// NA TELA (o motivo de `startCollapsed` existir). No editor de Texto, sem
// `startCollapsed`, nada muda: continua crescendo livre, com o modo tela
// cheia como via de escape para documento grande.
/**
 * ⚠️ O teto acompanha o estado do colapso.
 *
 * Antes ele era `minHeight` SEMPRE, e o colapso mexia só no piso. Num texto
 * curto isso funcionava por acidente — a caixa parava no conteúdo. Num texto
 * longo, `.rte-content` é `flex: 1 1 auto` e cresce até o teto: colapsada e
 * expandida davam a MESMA altura, e a seta não fazia nada visível. Era o caso
 * do editor de Texto, onde o conteúdo é o livro inteiro.
 *
 * Com o teto seguindo o estado, colapsar de fato encolhe, e a rolagem interna
 * atende o resto.
 */
const contentMaxHeight = computed(() => {
  if (isExpanded.value) return undefined;
  if (props.startCollapsed) {
    return isCollapsed.value ? collapsedHeight.value : props.minHeight;
  }
  return props.maxHeight ?? undefined;
});

const editor = useEditor({
  content: props.modelValue,
  editable: !props.disabled,
  extensions: buildEditorExtensions({
    placeholder: props.placeholder,
    unwrapPastedLineBreaks: props.unwrapPastedLineBreaks,
    // Quem sabe QUAL heurística aplicar é aqui, não a extensão.
    pasteTransform: unwrapPdfLineBreaks,
  }),
  // `content` acima é um retrato do valor no SETUP. Quando o formulário carrega
  // dados com `await`, o editor pode ser montado antes de o conteúdo chegar — e
  // aí aquele retrato é vazio.
  //
  // `onCreate` lê o valor no instante em que o editor fica pronto, que é mais
  // tarde. É rede independente do watch abaixo: uma cobre o editor nascendo
  // depois do valor, a outra o valor chegando depois do editor.
  onCreate: ({ editor: instance }) => {
    const value = props.modelValue ?? "";
    if (value && value !== instance.getHTML()) {
      instance.commands.setContent(value, { emitUpdate: false });
    }
  },
  onUpdate: ({ editor: instance }) => {
    emit("update:modelValue", instance.getHTML());
  },
});

/**
 * Contagem pela PROJEÇÃO, não pelo HTML.
 *
 * É o número que a cota do usuário usa e sobre o qual os offsets das notas são
 * medidos. Contar caracteres de HTML mostraria um valor inflado e sem
 * significado — `<p><strong>` não é conteúdo que alguém escreveu.
 */
const projectionLength = computed(() => {
  const html = props.modelValue;
  if (!html) return 0;
  try {
    return toPlainText(html).text.length;
  } catch {
    return 0;
  }
});

const isOverLimit = computed(
  () => props.maxLength != null && projectionLength.value > props.maxLength
);

// Mudança vinda de FORA (reset de formulário, carga tardia).
//
// Observa a INSTÂNCIA junto com o valor, e não só o valor. O motivo é concreto: o
// `useEditor` cria o editor de forma assíncrona, e um formulário que carrega dados
// com `await` pode preencher o `modelValue` ANTES de a instância existir. Vigiando
// só o valor, esse conteúdo chegaria com `editor.value` nulo e seria descartado em
// silêncio — o editor abriria vazio com o texto já carregado ao lado.
//
// `immediate` cobre o caminho inverso: instância criada depois, valor já presente.
//
// O teste de igualdade evita o laço: sem ele, cada emissão do onUpdate voltaria
// como prop nova, recolocaria o conteúdo e o cursor saltaria para o fim a cada
// tecla.
watch(
  [editor, () => props.modelValue],
  ([instance, value]) => {
    if (!instance) return;
    if ((value ?? "") === instance.getHTML()) return;
    instance.commands.setContent(value ?? "", { emitUpdate: false });
  },
  { immediate: true }
);

// Trocar de editável para bloqueado (ou o contrário) NÃO é mudança de conteúdo.
//
// O segundo argumento do `setEditable` é `emitUpdate`, e o padrão dele é `true`:
// sem o `false` explícito, o Tiptap dispara um evento `update` só por causa da
// troca de estado, o `onUpdate` acima devolve o `getHTML()` DAQUELE instante, e
// o v-model do pai passa a valer aquilo.
//
// Era assim que a importação de arquivo se perdia. No AddTextForm (e no
// EditTextForm) a caixa fica `:disabled="isExtracting"` enquanto o servidor
// extrai; ao terminar, o MESMO tique atribui o conteúdo e devolve `isExtracting`
// para `false`. Como `:disabled` vem antes do `v-model` no template, este watch
// corria primeiro, emitia o `<p></p>` do editor ainda vazio, o pai sobrescrevia
// com isso o HTML recém-extraído — e o watch do `modelValue` recolocava o vazio
// no editor. O arquivo era lido, a resposta vinha 200, e a caixa ficava branca.
watch(
  () => props.disabled,
  (value) => editor.value?.setEditable(!value, false)
);

function toggleExpand() {
  if (props.disabled) return;
  isExpanded.value = !isExpanded.value;
}

// Sair do modo expandido com Esc é esperado por quem usa tela cheia.
function onKeydown(event) {
  if (event.key === "Escape" && isExpanded.value) {
    isExpanded.value = false;
  }
}

onBeforeUnmount(() => {
  // useEditor já destrói no unmount; explicitar aqui não faz mal e documenta.
  editor.value?.destroy();
});

defineExpose({ editor, isExpanded, toggleExpand });
</script>

<template>
  <div
    class="rte"
    :class="{ 'rte--expanded': isExpanded, 'rte--disabled': disabled }"
    @keydown="onKeydown"
  >
    <!-- O botão de expandir entra pelo slot do fim da barra: o estado dele
         (`isExpanded`) é deste componente, não da barra. O divisor que o precede
         fica do lado da barra, para herdar o escopo de estilo dela. -->
    <RichTextToolbar :editor="editor" :disabled="disabled">
      <template #end>
        <button
          type="button"
          class="rte-expand"
          :title="isExpanded ? 'Reduzir (Esc)' : 'Expandir'"
          :aria-label="isExpanded ? 'Reduzir editor' : 'Expandir editor'"
          @click="toggleExpand"
        >
          <i :class="isExpanded ? 'fas fa-compress' : 'fas fa-expand'"></i>
        </button>
      </template>
    </RichTextToolbar>

    <!--
      A área editável recebe .text-body--html para que o que se escreve tenha a
      MESMA aparência do que se lê. O CSS daquela classe é global (TextDisplayer)
      e carrega o vocabulário de oito classes.
    -->
    <EditorContent
      class="rte-content text-body--html"
      :editor="editor"
      :style="{ minHeight: contentMinHeight, maxHeight: contentMaxHeight }"
    />

    <!-- Seta do colapso inicial (startCollapsed) — agora um toggle: aponta
         para baixo para expandir, vira para cima (e a caixa some para o
         tamanho de largada) quando já está no tamanho final. -->
    <button
      v-if="props.startCollapsed && !isExpanded"
      type="button"
      class="rte-collapse-toggle"
      :aria-label="isCollapsed ? 'Expandir editor' : 'Colapsar editor'"
      :title="isCollapsed ? 'Expandir' : 'Colapsar'"
      @mousedown.prevent
      @click="toggleCollapse"
    >
      <i
        :class="isCollapsed ? 'fas fa-chevron-down' : 'fas fa-chevron-up'"
        aria-hidden="true"
      ></i>
    </button>

    <!-- O número é o da projeção: é ele que a cota usa. -->
    <div class="rte-footer">
      <span :class="{ 'rte-over': isOverLimit }">
        {{ projectionLength.toLocaleString("pt-BR") }}
        <template v-if="maxLength">
          / {{ maxLength.toLocaleString("pt-BR") }}
        </template>
        caracteres
      </span>
    </div>
  </div>
</template>

<style scoped>
/* ⚠️ Sem `overflow: hidden` aqui, de propósito. Ele recortava os cantos, mas
   `overflow` diferente de `visible` num ancestral ANULA o `position: sticky` da
   barra de ferramentas — e a barra precisa acompanhar a rolagem. O arredondamento
   fica por conta dos filhos. */
/* Fundo PRÓPRIO, não herdado.
   
   Com `inherit`, a área editável pegava a cor de quem embutisse o editor — no
   leitor, o creme da folha de leitura — enquanto a barra e o rodapé pintavam
   branco por conta própria (`var(--rte-bg, var(--surface))`). A caixa saía com três faixas
   de duas cores. Agora a mesma variável governa o editor inteiro, e quem quiser
   outra cor troca a variável em vez de repintar cada parte. */
/* A caixa é definida por SOMBRA, não por contorno.
   
   As quatro linhas externas competiam com as internas — a da barra e a do painel
   —, e num editor branco sobre folha creme o contorno era o elemento mais escuro
   da tela. A sombra separa o mesmo tanto sem desenhar nada: a caixa passa a
   flutuar em vez de estar riscada. */
.rte {
  position: relative; /* âncora da seta de colapso, ver .rte-collapse-toggle */
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;
  background: var(--rte-bg, var(--surface));
  box-shadow: var(--shadow-sm),
    0 3px 10px rgba(15, 23, 42, 0.06);
}

/* O fundo segue o tema sozinho (`--surface`). No escuro só a sombra muda: ela
   precisa ser mais forte, porque sombra escura sobre fundo escuro quase não
   aparece, e sem ela a caixa se dissolveria na página. */
.dark .rte {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.35), 0 3px 12px rgba(0, 0, 0, 0.4);
}

.rte--disabled {
  opacity: 0.65;
}

/* Modo expandido: editar um livro numa caixa de seis linhas é ruim. */
.rte--expanded {
  position: fixed;
  inset: 0;
  z-index: 10000;
  border-radius: 0;
  background: var(--rte-bg, var(--surface));
}

/* Coluna flexível para o `.ProseMirror` PREENCHER a caixa.
   
   Antes ele ficava só com a altura do próprio conteúdo — uma linha, no editor
   vazio — e todo o resto da área era espaço morto: clicar ali não caía no
   elemento editável, então o cursor não aparecia. A regra que tentava resolver
   isso era `min-height: 100%` no ProseMirror, e porcentagem depende da altura do
   pai estar resolvida; aqui a altura vem de um `min-height`, e o pai continua com
   `height: auto`. Esticar por flex não depende dessa sutileza. */
.rte-content {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 0.85rem 1rem;
}

/* Mesma caixa dos botões da barra: ele é um deles. */
.rte-expand {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  height: 2rem;
  border-radius: 0.35rem;
  background: transparent;
  border: 1px solid transparent;
  color: inherit;
  cursor: pointer;
  font-size: 0.85rem;
}

.rte-expand:hover {
  background: rgba(128, 128, 128, 0.16);
}

/* Separado do conteúdo por TOM, não por linha.
   
   A área editável é branca; o rodapé é cinza. A diferença de fundo já diz que
   ele é moldura, e uma linha por cima disso seria dizer duas vezes.
   
   A opacidade fica no texto, nunca no elemento. No elemento ela apagaria também
   o fundo — e fundo translúcido deixa o conteúdo aparecer por trás, que é
   justamente o que a moldura não pode fazer. */
.rte-footer {
  display: flex;
  justify-content: flex-end;
  padding: 0.3rem 0.6rem;
  border-radius: 0 0 0.45rem 0.45rem;
  background: var(--rte-footer-bg, var(--surface-sunken));
  font-size: 0.75rem;
}

.rte-footer span {
  opacity: 0.7;
}

/* Mais específico que a regra acima de propósito: o aviso de limite não pode
   ficar apagado junto com o contador. */
.rte-footer .rte-over {
  color: var(--danger-text);
  font-weight: 600;
  opacity: 1;
}

/* `--surface-sunken` fica um tom ABAIXO da superfície nos dois temas: no claro o
   cinza recua diante do branco, no escuro o rodapé mais escuro lê como recuado. */

/* Seta do colapso inicial — pousa na BORDA de baixo da caixa, meio para fora,
   igual a um "mostrar mais" de card: precisa ler como afordance separada do
   rodapé (contador), não como mais um item da barra. Sombra em vez de
   contorno, pelo mesmo motivo já registrado para `.rte`. */
.rte-collapse-toggle {
  position: absolute;
  left: 50%;
  bottom: -0.7rem;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.9rem;
  height: 1.9rem;
  border-radius: 999px;
  border: none;
  background: var(--rte-bg, var(--surface));
  color: inherit;
  cursor: pointer;
  font-size: 0.78rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08), 0 3px 8px rgba(15, 23, 42, 0.14);
  z-index: 3;
  transition: transform 0.15s ease;
}
.rte-collapse-toggle:hover {
  transform: translateX(-50%) translateY(2px);
}
</style>

<style>
/* Não-scoped: o ProseMirror monta o conteúdo fora do escopo do componente. */
/* `flex: 1` no lugar de `min-height: 100%` — ver o comentário de `.rte-content`.
   É o que garante que a área editável ocupe a caixa inteira, e portanto que um
   clique em qualquer ponto dela ponha o cursor no texto. */
.rte-content .ProseMirror {
  outline: none;
  flex: 1 1 auto;
}

/* ── Redimensionar coluna ────────────────────────────────────────────────────
   O Tiptap injeta um .column-resize-handle na divisa da célula. Por padrão ele é
   invisível, então não há como saber que dá para arrastar.

   Aqui a divisa engrossa e ganha cor ao passar o mouse sobre a tabela — o sinal
   fica ligado ao gesto, sem poluir a tabela em repouso. */
/* As regras da tabela redimensionável NÃO ficam aqui: estão em assets/main.css,
   presas ao seletor `.ProseMirror`. Ver o comentário de lá — o motivo é que
   depender de `.rte-content` acrescenta uma suposição sobre onde o Vue coloca a
   classe, e o handle é justamente o elemento que não pode falhar em ficar fora do
   fluxo. */

/* ── Imagem selecionada ──────────────────────────────────────────────────────
   Clicar numa imagem cria uma NodeSelection, e o ProseMirror marca o nó com
   `.ProseMirror-selectednode` — verificado, a classe cai no próprio <img>.

   Sem esta regra o clique não dava retorno NENHUM sobre a imagem: a seleção
   existia de fato (o botão de imagem da barra acendia, o de remover destravava),
   mas na tela nada mudava, e não havia como saber que a imagem estava pega.

   Cursor de texto não serve aqui: a imagem é um bloco atômico, não se digita
   dentro dela.

   `outline`, e não `border`: contorno entra na caixa e empurraria o texto em
   volta a cada seleção — a linha inteira dançaria a cada clique. `outline` fica
   fora do fluxo. O azul é o mesmo de `.rte-btn--active`, pelo mesmo motivo de
   sempre: é o azul que este editor usa para dizer "ativo agora".

   Só no EDITOR: no leitor não há seleção de nó, e por isso a regra vive aqui e
   não no CSS compartilhado de `.text-body--html`. */
.rte-content .ProseMirror img.ProseMirror-selectednode,
.rte-content .ProseMirror figure.ProseMirror-selectednode {
  outline: 2px solid rgba(59, 130, 246, 0.9);
  outline-offset: 2px;
  border-radius: 2px;
}

/* A imagem é clicável e arrastável; o cursor de seta padrão não diz isso.
   Na figura o alvo é a figura INTEIRA (ela é `atom`), legenda incluída — por
   isso o cursor vai nela, e não só na imagem dentro dela. */
.rte-content .ProseMirror img,
.rte-content .ProseMirror figure {
  cursor: pointer;
}

/* A legenda não é editável no lugar: quem edita é o campo do painel. Sem isto o
   texto convida a um clique que não faz nada. */
.rte-content .ProseMirror figcaption {
  user-select: none;
}

/* ── Âncora ──────────────────────────────────────────────────────────────────
   Alvo de link interno: `<a id="…"></a>`, sem largura e sem texto. Vem da
   importação de EPUB, onde é o ponto para onde a chamada de nota e o sumário
   apontam (ver `LitteraeAnchor`).

   SÓ NO EDITOR, e é o ponto inteiro desta regra: no leitor a âncora não pode
   aparecer — ela não é conteúdo, é destino. O escopo `.rte-content .ProseMirror`
   garante isso sozinho, porque o leitor monta `.text-body--html` sem esse
   ancestral. Mesma separação do contorno de imagem selecionada, logo acima.

   Sem a marca, quem edita não vê que há algo ali: o cursor pousa dos dois lados,
   um Backspace apaga o nó sem mudar nada na tela, e o retorno da nota de rodapé
   passa a apontar para um alvo que não existe mais — sem aviso e sem desfazer.

   Selecionada pela FORMA (`a[id]:empty`), não por classe: classe fora do
   vocabulário fechado seria arrancada pelo normalizador ao salvar, e a marca
   sumiria no primeiro salvamento. `:empty` também é o que impede a regra de
   pegar link comum.

   O sinal é pintado por `::after` ABSOLUTO. O nó tem largura zero, e marca que
   ocupasse espaço empurraria o texto — no 1889 são 841 âncoras, e a quebra de
   linha do editor deixaria de bater com a do leitor.

   Como não ocupa espaço, ele é ERGUIDO acima da linha: no corpo da linha
   cobriria a letra vizinha (a chamada de nota vem logo depois), e na entrelinha
   há folga sobrando. Miúdo e apagado pelo mesmo motivo — ele diz "há algo
   aqui", não disputa a leitura. */
.rte-content .ProseMirror a[id]:empty {
  position: relative;
}

.rte-content .ProseMirror a[id]:empty::after {
  /* \f13d é o fa-anchor do Font Awesome 6, o mesmo conjunto que a interface usa
     — e o mesmo caminho do ícone da moldura de imagem em main.css. */
  content: "\f13d";
  font-family: "Font Awesome 6 Free";
  font-weight: 900;
  font-size: 0.55em;
  line-height: 1;

  position: absolute;
  top: -0.5em;
  left: -0.25em;
  color: currentColor;
  opacity: 0.4;
  pointer-events: none;
}

/* Selecionada, a âncora acende — é o retorno de que o clique pegou o nó, já que
   ele não tem conteúdo para inverter como o texto faz. O azul é o mesmo de
   `.rte-btn--active`, o que este editor usa para dizer "ativo agora".

   `color`, e não `background`: agora o sinal é um glifo, e quem o pinta é a cor
   do texto. Fundo colorido desenharia um retângulo atrás dele. */
.rte-content .ProseMirror a[id]:empty.ProseMirror-selectednode::after {
  color: rgba(59, 130, 246, 0.9);
  opacity: 1;
}

/* Placeholder do Tiptap — decoração, não entra no getHTML(). */
.rte-content .ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
  opacity: 0.45;
}
</style>
